import { useTranslation } from "react-i18next";
import {
  DISCOUNT_TYPES,
  EDITORIAL_CALLOUT_TYPES,
  EDITORIAL_CLOSING_THEMES,
  EDITORIAL_ICONS,
  EDITORIAL_THEMES,
  type DiscountType,
  type EditorialCalloutType,
  type EditorialIcon,
  type EditorialTheme,
} from "@nh/shared";
import type { SelectOption } from "@/shared/ui/core";

/**
 * Los desplegables del formulario, traducidos, en un solo sitio.
 *
 * Cada mapa está tipado como `Record<…, string>` sobre la lista CERRADA
 * del contrato. Es la red: si mañana el contrato añade un tipo de
 * producto o un icono y aquí no se pone su etiqueta, el error sale en
 * `npm run typecheck` —hoy— y no en un desplegable con una opción en
 * blanco que nadie sabe qué significa.
 *
 * El ORDEN lo pone el contrato y no el orden en que se escribieron las
 * etiquetas: así la lista es la misma en el formulario, en los filtros y
 * en cualquier pantalla que llegue después.
 */

/*
 * ── Las etiquetas de los TIPOS ya no viven aquí ────────────────────
 *
 * Eran un `Record<ProductType, string>` sobre el enum cerrado, y esa
 * anotación era una red: el día que el contrato añadiera un tipo sin
 * etiqueta, `npm run typecheck` lo cazaba.
 *
 * El tipo de ítem es ahora un catálogo que administra quien vende, así
 * que su etiqueta la escribe la misma persona que crea el tipo, en los
 * dos idiomas, y viaja en el documento. Un mapa aquí no podría cubrir
 * los tipos que todavía no existen, y las nueve etiquetas de sistema
 * viven donde están sus valores: `SYSTEM_PRODUCT_TYPES` en el contrato,
 * de donde el seed las siembra.
 *
 * Lo que pinta el desplegable es `CatalogSelectField`.
 */

const discountTypeLabels: Record<DiscountType, string> = {
  percent: "Porcentaje",
  fixed: "Monto fijo",
};

const editorialThemeLabels: Record<EditorialTheme, string> = {
  light: "Claro",
  dark: "Oscuro",
};

const closingThemeLabels: Record<(typeof EDITORIAL_CLOSING_THEMES)[number], string> = {
  accent: "Acento",
  dark: "Oscuro",
  light: "Claro",
};

const calloutTypeLabels: Record<EditorialCalloutType, string> = {
  science: "Ciencia",
  warning: "Advertencia",
  context: "Contexto",
  quote: "Cita",
};

/**
 * Los doce iconos, con nombre en español.
 *
 * El VALOR es el nombre del contrato (`"bolt"`), que es lo que viaja y
 * lo que el frontend de la tienda resuelve a un trazo. La etiqueta es
 * para quien elige: "bolt" no dice nada a quien escribe una ficha de
 * suplementos, "Rayo" sí.
 */
const editorialIconLabels: Record<EditorialIcon, string> = {
  bolt: "Rayo",
  dna: "ADN",
  shield: "Escudo",
  leaf: "Hoja",
  heart: "Corazón",
  brain: "Cerebro",
  flask: "Matraz",
  sparkles: "Destellos",
  clock: "Reloj",
  droplet: "Gota",
  sun: "Sol",
  activity: "Pulso",
  atom: "Átomo",
  balance: "Balanza",
  berry: "Baya",
  cell: "Célula",
  eye: "Ojo",
  flower: "Flor",
  globe: "Globo",
  grape: "Uva",
  leaves: "Hojas",
  link: "Enlace",
  magnet: "Imán",
  marigold: "Caléndula",
  microscope: "Microscopio",
  palm: "Palma",
  pomegranate: "Granada",
  pulse: "Latido",
  refresh: "Renovación",
  spark: "Chispa",
  spiral: "Espiral",
  target: "Diana",
};

/** Convierte una lista cerrada del contrato en opciones traducidas. */
function useOptions<T extends string>(
  values: readonly T[],
  labels: Record<T, string>,
): SelectOption[] {
  const { t } = useTranslation();
  return values.map((value) => ({ value, label: t(labels[value]) }));
}

export function useDiscountTypeOptions(): SelectOption[] {
  return useOptions(DISCOUNT_TYPES, discountTypeLabels);
}

export function useEditorialThemeOptions(): SelectOption[] {
  return useOptions(EDITORIAL_THEMES, editorialThemeLabels);
}

export function useClosingThemeOptions(): SelectOption[] {
  return useOptions(EDITORIAL_CLOSING_THEMES, closingThemeLabels);
}

export function useCalloutTypeOptions(): SelectOption[] {
  return useOptions(EDITORIAL_CALLOUT_TYPES, calloutTypeLabels);
}

export function useEditorialIconOptions(): SelectOption[] {
  return useOptions(EDITORIAL_ICONS, editorialIconLabels);
}
